/**
 * <EdgeRitualPlayer />
 *
 * Cinematic, audio-driven phrase overlay for THE EDGE — Guided
 * Performance Rituals. Each spoken line lands on the screen at its
 * exact audio second (verified via Whisper segment timestamps).
 *
 * Design principles (premium dark-mode):
 *   - No buttons mid-flight. The audio is the source of truth.
 *   - One phrase visible at a time, fading in/out gracefully.
 *   - Soft amber glow + subtle particle background — feels expensive.
 *   - Big top-bar progress, 1 small mute toggle, 1 close button.
 *
 * Props:
 *   audioUrl        primary audio (intro track)
 *   loopAudioUrl    optional loop audio (hybrid identity loop)
 *   phrases         [{ start, text, tone }]   — for primary track
 *   loopPhrases     [{ start, text, tone }]   — relative to loop track
 *   accentColor     CSS colour for the amber glow
 *   onClose         callback when user closes
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Volume2, VolumeX, Share2, Download, Check, Pause, Play } from "lucide-react";
import html2canvas from "html2canvas";
import { voiceVolume, musicVolume, getStoredAudioMode } from "../../utils/modeAudio";
import useTrackPhases from "../../hooks/useTrackPhases";

const TONE_STYLES = {
  soft: {
    fontWeight: 300,
    color: "rgba(255,255,255,0.78)",
    fontSize: "clamp(22px, 4.4vw, 32px)",
    fontStyle: "italic",
    textShadow: "0 4px 28px rgba(217,165,90,0.18)",
    letterSpacing: "0.005em",
  },
  imagine: {
    fontWeight: 300,
    color: "rgba(255,235,200,0.92)",
    fontSize: "clamp(24px, 4.8vw, 36px)",
    textShadow: "0 6px 32px rgba(217,165,90,0.30)",
    letterSpacing: "0.01em",
  },
  identity: {
    fontWeight: 500,
    color: "rgba(255,225,180,1)",
    fontSize: "clamp(28px, 5.6vw, 44px)",
    textShadow: "0 8px 38px rgba(217,165,90,0.55), 0 0 60px rgba(217,165,90,0.18)",
    letterSpacing: "0.012em",
  },
  closing: {
    fontWeight: 400,
    color: "rgba(255,235,200,0.95)",
    fontSize: "clamp(26px, 5.2vw, 40px)",
    textShadow: "0 6px 32px rgba(217,165,90,0.42)",
    letterSpacing: "0.018em",
  },
};

function pickActivePhrase(phrases, t) {
  let cur = null;
  for (const p of phrases) {
    if (t >= p.start) cur = p;
    else break;
  }
  return cur;
}

export default function EdgeRitualPlayer({
  audioUrl,
  loopAudioUrl,
  bgMusicUrl,
  bgMusicVolume = 0.18,
  phrases = [],
  loopPhrases = [],
  // Optional Whisper override track ids — when provided, the player
  // fetches `/api/audio/phases/<trackId>` and uses the word-level phrase
  // markers in preference to the hand-tuned `phrases` / `loopPhrases`
  // arrays. Falls back cleanly to the hardcoded list if no override exists.
  trackId,
  loopTrackId,
  accentColor = "rgba(217,165,90,0.55)",
  title,
  description,
  duration,
  onClose,
}) {
  const audioRef     = useRef(null);
  const loopRef      = useRef(null);
  const bgMusicRef   = useRef(null);
  const shareCardRef = useRef(null);
  const [now,        setNow]        = useState(0);
  const [muted,      setMuted]      = useState(false);
  const [paused,     setPaused]     = useState(false);
  const [introEnded, setIntroEnded] = useState(false);
  const [audioDur,   setAudioDur]   = useState(duration || 0);
  const [showShare,  setShowShare]  = useState(false);
  const [shareState, setShareState] = useState("idle"); // 'idle'|'rendering'|'shared'|'error'

  // Hard-stop ALL audio on unmount. Without this the spoken voice + bg music
  // kept playing after the user navigated to /home — the recurring "audio
  // still plays" complaint. Critical that this runs on every component
  // teardown, regardless of whether handleClose was called.
  useEffect(() => () => {
    [audioRef, loopRef, bgMusicRef].forEach((r) => {
      const el = r.current;
      if (!el) return;
      try { el.pause(); el.muted = true; el.removeAttribute('src'); el.load?.(); } catch (_) { /* noop */ }
    });
  }, []);

  // Backstop — pagehide hard-stops audio on tab close/reload;
  // visibilitychange→hidden does a SOFT pause so a brief notification check
  // doesn't kill the session.
  useEffect(() => {
    const hardStop = () => {
      [audioRef, loopRef, bgMusicRef].forEach((r) => {
        const el = r.current;
        if (!el) return;
        try { el.pause(); el.muted = true; el.removeAttribute('src'); } catch (_) { /* noop */ }
      });
    };
    const onVis = () => {
      if (document.visibilityState !== 'hidden') return;
      [audioRef, loopRef, bgMusicRef].forEach((r) => {
        const el = r.current;
        if (el && !el.paused) try { el.pause(); } catch (_) { /* noop */ }
      });
    };
    window.addEventListener('pagehide', hardStop);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('pagehide', hardStop);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  // Start primary audio on mount.
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !audioUrl) return;
    const voiceTarget = voiceVolume(getStoredAudioMode());
    el.volume = voiceTarget;
    el.play().catch(() => {});
    const onMeta = () => setAudioDur(el.duration || duration || 0);
    const onTime = () => setNow(el.currentTime || 0);
    const onEnd  = () => {
      if (loopAudioUrl && loopRef.current) {
        loopRef.current.loop = true;
        loopRef.current.volume = voiceTarget;
        loopRef.current.play().catch(() => {});
        setIntroEnded(true);
      }
    };
    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("timeupdate",     onTime);
    el.addEventListener("ended",          onEnd);
    return () => {
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("timeupdate",     onTime);
      el.removeEventListener("ended",          onEnd);
    };
  }, [audioUrl, loopAudioUrl, duration]);

  // While in loop mode, drive `now` from the loop element so loop
  // phrases stay in sync each pass.
  useEffect(() => {
    if (!introEnded) return;
    const el = loopRef.current;
    if (!el) return;
    const onTime = () => setNow(el.currentTime || 0);
    el.addEventListener("timeupdate", onTime);
    return () => el.removeEventListener("timeupdate", onTime);
  }, [introEnded]);

  // Mute toggle drives both elements.
  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
    if (loopRef.current)  loopRef.current.muted  = muted;
    if (bgMusicRef.current) bgMusicRef.current.muted = muted;
  }, [muted]);

  // Background music — soft cinematic underscore that fades in over
  // ~4s, loops, and fades out the moment the player unmounts (handled
  // by the cleanup in the audio start effect below).
  useEffect(() => {
    const el = bgMusicRef.current;
    if (!el || !bgMusicUrl) return;
    // Background music respects the global Clear / Soft / Deep mode,
    // falling back to the prop-provided bgMusicVolume.
    const targetVol = Math.min(bgMusicVolume, musicVolume(getStoredAudioMode()) + 0.04);
    el.loop = true;
    el.volume = 0;
    el.play().catch(() => {});
    let v = 0;
    const id = setInterval(() => {
      v = Math.min(targetVol, v + targetVol / 18);
      try { el.volume = v; } catch (_) {}
      if (v >= targetVol) clearInterval(id);
    }, 220);
    return () => {
      clearInterval(id);
      try { el.pause(); } catch (_) {}
    };
  }, [bgMusicUrl, bgMusicVolume]);

  const activeList = introEnded && loopPhrases.length ? loopPhrases : phrases;
  // Whisper word-level overrides — when populated, use these instead of
  // the hardcoded phrase list so each spoken word lands on screen at its
  // exact spoken moment (CapCut auto-cue) site-wide.
  const introOverride = useTrackPhases(trackId, { enabled: !!trackId });
  const loopOverride = useTrackPhases(loopTrackId, { enabled: !!loopTrackId });
  const overrideList = (introEnded && loopOverride.phrases) ? loopOverride.phrases
                       : (!introEnded && introOverride.phrases) ? introOverride.phrases
                       : null;
  const renderList = overrideList || activeList;
  // Pick the active phrase — same rule for both modes (latest start <= now).
  const active = useMemo(() => {
    if (!renderList || !renderList.length) return null;
    let cur = null;
    for (const p of renderList) {
      if (now >= p.start) cur = p;
      else break;
    }
    return cur;
  }, [renderList, now]);
  // For word-level animation we need the index so we can find the next phrase's start.
  const activeIdx = useMemo(() => {
    if (!active || !renderList) return -1;
    return renderList.findIndex((p) => p.start === active.start && p.text === active.text);
  }, [active, renderList]);
  const nextPhraseStart = activeIdx >= 0 && activeIdx < (renderList?.length || 0) - 1
    ? renderList[activeIdx + 1].start
    : null;

  const totalKnown = audioDur && audioDur > 1 ? audioDur : (duration || 0);
  const progress = !introEnded && totalKnown
    ? Math.min(100, (now / totalKnown) * 100)
    : (introEnded ? 100 : 0);

  const handleClose = () => {
    [audioRef, loopRef, bgMusicRef].forEach((r) => {
      const el = r.current;
      if (!el) return;
      try { el.pause(); el.muted = true; el.removeAttribute('src'); el.load?.(); } catch (_) { /* noop */ }
    });
    onClose?.();
  };

  // Toggle pause/resume across all currently-active audio elements
  // (intro, identity loop, bg music). Pause freezes everything; resume
  // restarts whichever stream was active.
  const togglePause = () => {
    const main = introEnded ? loopRef.current : audioRef.current;
    if (!main) { setPaused((p) => !p); return; }
    if (main.paused) {
      main.play().catch(() => {});
      if (bgMusicRef.current && bgMusicRef.current.paused) bgMusicRef.current.play().catch(() => {});
      setPaused(false);
    } else {
      try { main.pause(); } catch (_) { /* noop */ }
      try { bgMusicRef.current && bgMusicRef.current.pause(); } catch (_) { /* noop */ }
      setPaused(true);
    }
  };

  // Capture the share card and either invoke the Web Share API
  // (with a file blob, ideal on iOS/Android) or fall back to a PNG
  // download. The share card is rendered off-screen at -9999px so the
  // user never sees it visually, but html2canvas can still snapshot it.
  const handleShare = async () => {
    if (!shareCardRef.current) return;
    setShareState("rendering");
    try {
      const canvas = await html2canvas(shareCardRef.current, {
        backgroundColor: null,
        scale: 2,
        logging: false,
        useCORS: true,
      });
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png", 0.95));
      const filename = `the-edge-${(title || "moment").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.png`;

      // Try the native share sheet first (mobile + some desktop).
      const file = new File([blob], filename, { type: "image/png" });
      const navAny = typeof navigator !== "undefined" ? navigator : null;
      if (
        navAny &&
        typeof navAny.share === "function" &&
        navAny.canShare &&
        navAny.canShare({ files: [file] })
      ) {
        await navAny.share({
          title: "The Edge",
          text: `${title} — Reclaim`,
          files: [file],
        });
      } else {
        // Fallback: trigger a download.
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1500);
      }
      setShareState("shared");
      setTimeout(() => setShareState("idle"), 1800);
    } catch (err) {
      console.warn("[EdgeRitualPlayer] share failed", err);
      setShareState("error");
      setTimeout(() => setShareState("idle"), 1800);
    }
  };

  return (
    <div
      className="fixed inset-0 overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse at 50% 30%, #1A1410 0%, #0E0B08 55%, #050402 100%)",
      }}
      data-testid="edge-ritual-player"
    >
      {/* Subtle amber atmosphere — slow drifting glow */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          background: [
            `radial-gradient(circle at 30% 25%, ${accentColor.replace("0.55", "0.10")} 0%, transparent 60%)`,
            `radial-gradient(circle at 70% 35%, ${accentColor.replace("0.55", "0.10")} 0%, transparent 60%)`,
            `radial-gradient(circle at 30% 25%, ${accentColor.replace("0.55", "0.10")} 0%, transparent 60%)`,
          ],
        }}
        transition={{ duration: 24, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Top bar — tiny title + close */}
      <div className="absolute top-0 inset-x-0 flex items-center justify-between px-5 py-4 z-20">
        <div className="flex items-center gap-3 min-w-0">
          <span
            className="text-[10px] uppercase tracking-[0.4em] text-amber-500/80"
            style={{ fontFamily: "Inter, system-ui" }}
          >
            The Edge
          </span>
          <span className="text-white/30 text-xs truncate max-w-[55vw]">
            {title}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setMuted((m) => !m)}
            className="text-white/45 hover:text-white/85 transition-colors"
            data-testid="edge-ritual-mute-btn"
            aria-label={muted ? "Unmute" : "Mute"}
          >
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          <button
            onClick={togglePause}
            className="text-white/45 hover:text-white/85 transition-colors"
            data-testid="edge-ritual-pause-btn"
            aria-label={paused ? "Resume" : "Pause"}
          >
            {paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
          </button>
          <button
            onClick={handleClose}
            className="text-white/45 hover:text-white/85 transition-colors"
            data-testid="edge-ritual-close-btn"
            aria-label="Close ritual"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Top progress hairline */}
      <div className="absolute top-12 inset-x-0 h-px bg-white/[0.06] z-10">
        <motion.div
          className="h-full"
          style={{
            background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)`,
            width: introEnded ? "100%" : `${progress}%`,
          }}
          transition={{ duration: 0.2 }}
        />
      </div>

      {/* Centre stage — phrase. Words light up at their actual spoken
          moments when Whisper word-level data is present (override list);
          otherwise the whole phrase fades in as a sentence. */}
      <div className="absolute inset-0 flex items-center justify-center px-8">
        <AnimatePresence mode="wait">
          {active && (
            <motion.p
              key={`${active.start}-${active.text}`}
              initial={{ opacity: 0, y: 10, filter: "blur(8px)" }}
              animate={{ opacity: 1, y: 0,  filter: "blur(0px)" }}
              exit={{    opacity: 0, y: -8, filter: "blur(6px)" }}
              transition={{ duration: 1.05, ease: [0.22, 1, 0.36, 1] }}
              className="text-center max-w-[22ch]"
              style={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                lineHeight: 1.3,
                ...TONE_STYLES[active.tone || "soft"],
              }}
              data-testid={`edge-ritual-phrase-${Math.round(active.start)}`}
            >
              {(() => {
                // Build word→time pairs once per active phrase. When the
                // override array carries `words[]`, each word lights up at
                // its real Whisper start. Otherwise we even-distribute across
                // the phrase span so the cadence still tracks the sentence.
                const tokens = (active.text || '').split(/\s+/).filter(Boolean);
                const ws = Array.isArray(active.words) ? active.words : [];
                const t0 = active.start || 0;
                const tEnd = (Number.isFinite(active.end) ? active.end : null)
                  || (Number.isFinite(nextPhraseStart) ? nextPhraseStart : null)
                  || (t0 + tokens.length * 0.42);
                const span = Math.max(0.0001, tEnd - t0);
                return tokens.map((tok, i) => {
                  const start = (ws[i] && Number.isFinite(ws[i].start))
                    ? ws[i].start
                    : t0 + (i / Math.max(tokens.length, 1)) * span;
                  const visible = now >= start;
                  return (
                    <motion.span
                      key={`${active.start}-${i}`}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: visible ? 1 : 0, y: visible ? 0 : 6 }}
                      transition={{ duration: 0.45, ease: 'easeOut' }}
                      className="inline-block mr-[0.28em]"
                    >
                      {tok}
                    </motion.span>
                  );
                });
              })()}
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      {/* Soft pulsing glyph at bottom — visual anchor */}
      <div className="absolute bottom-16 inset-x-0 flex items-center justify-center pointer-events-none">
        <motion.div
          className="rounded-full"
          style={{
            width: 6, height: 6,
            background: "rgba(217,165,90,0.85)",
            boxShadow: `0 0 18px ${accentColor}`,
          }}
          animate={{ scale: [1, 1.5, 1], opacity: [0.55, 1, 0.55] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Loop banner */}
      {introEnded && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 1 }}
          className="absolute bottom-6 inset-x-0 text-center pointer-events-none"
        >
          <span className="text-[10px] uppercase tracking-[0.4em] text-amber-500/55">
            Identity loop · stay as long as you like
          </span>
        </motion.div>
      )}

      {/* Share This Moment — appears once we're past the halfway mark
          OR we're in the identity loop. Becomes a fully styled
          alternative to the close button as the experience matures. */}
      {(progress > 50 || introEnded) && (
        <div className="absolute bottom-20 inset-x-0 flex justify-center z-30">
          <button
            onClick={handleShare}
            disabled={shareState === "rendering"}
            data-testid="edge-ritual-share-btn"
            className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[11px] uppercase tracking-[0.32em] transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50"
            style={{
              background:
                "linear-gradient(135deg, rgba(217,165,90,0.18), rgba(217,165,90,0.06))",
              border: "1px solid rgba(217,165,90,0.30)",
              color: "rgba(255,225,180,0.95)",
              boxShadow: "0 10px 28px -16px rgba(217,165,90,0.55)",
            }}
          >
            {shareState === "shared" ? (
              <>
                <Check className="w-3.5 h-3.5" />
                Shared
              </>
            ) : shareState === "rendering" ? (
              <>
                <span className="w-3.5 h-3.5 rounded-full border border-amber-300/60 border-t-transparent animate-spin" />
                Capturing
              </>
            ) : (
              <>
                <Share2 className="w-3.5 h-3.5" />
                Share This Moment
              </>
            )}
          </button>
        </div>
      )}

      {/* Off-screen share card — beautiful 1080×1080 capture target.
          Renders at -9999px so the user never sees it, but html2canvas
          can snapshot it on demand. */}
      <div
        ref={shareCardRef}
        aria-hidden
        style={{
          position: "absolute",
          left: -10000,
          top: 0,
          width: 1080,
          height: 1080,
          background:
            "radial-gradient(ellipse at 50% 28%, #1A1410 0%, #0E0B08 55%, #050402 100%)",
          padding: 96,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          color: "white",
          fontFamily: "Inter, system-ui",
        }}
      >
        <div>
          <p style={{
            fontSize: 24,
            letterSpacing: "0.5em",
            textTransform: "uppercase",
            color: "rgba(217,165,90,0.95)",
            fontWeight: 600,
          }}>The Edge</p>
          <p style={{
            fontSize: 22,
            color: "rgba(255,255,255,0.45)",
            marginTop: 18,
            letterSpacing: "0.02em",
          }}>{title}</p>
        </div>
        <div style={{ textAlign: "center", padding: "0 32px" }}>
          <p style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontSize: 88,
            fontWeight: 500,
            lineHeight: 1.18,
            color: "rgba(255,225,180,1)",
            textShadow: "0 8px 60px rgba(217,165,90,0.5)",
            letterSpacing: "0.012em",
          }}>
            {(active && active.text) || "Step forward"}
          </p>
          <div style={{
            marginTop: 64,
            height: 1,
            background: "linear-gradient(90deg, transparent, rgba(217,165,90,0.55), transparent)",
          }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
          <p style={{
            fontSize: 22,
            color: "rgba(255,255,255,0.5)",
            fontStyle: "italic",
          }}>
            State · Identity · Momentum
          </p>
          <p style={{
            fontSize: 22,
            color: "rgba(217,165,90,0.95)",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            fontWeight: 600,
          }}>
            Reclaim
          </p>
        </div>
      </div>

      {/* Hidden audio elements — emit both AAC/m4a and MP3 sources so
          Safari/iOS prefer m4a (smaller) while environments lacking AAC
          (e.g. headless Chromium, some Linux Chrome builds) gracefully
          fall back to MP3. */}
      <audio ref={audioRef} preload="auto" playsInline>
        {audioUrl && <source src={audioUrl.replace(/\.m4a$/, ".mp3")} type="audio/mpeg" />}
        {audioUrl && <source src={audioUrl} type="audio/mp4" />}
      </audio>
      {loopAudioUrl && (
        <audio ref={loopRef} preload="auto" playsInline>
          <source src={loopAudioUrl.replace(/\.m4a$/, ".mp3")} type="audio/mpeg" />
          <source src={loopAudioUrl} type="audio/mp4" />
        </audio>
      )}
      {bgMusicUrl && (
        <audio ref={bgMusicRef} preload="auto" playsInline loop src={bgMusicUrl} />
      )}
    </div>
  );
}
